import { z } from "zod";
import { albumLibrarySchema } from "@libs/catalog-engine-contracts/album";
import { playlistLibrarySchema } from "./playlist";
import { artistLibrarySchema } from "./artist";

/**
 * Rich library schema: items as they appear in the OFFLINE store, with
 * full song/artist data attached. Used by downloadManager to validate
 * offline-augmented items.
 */
export const librarySchema = z.discriminatedUnion("type", [
  albumLibrarySchema,
  playlistLibrarySchema,
  artistLibrarySchema,
]);

export type Library = z.infer<typeof librarySchema>;

/**
 * Lean library list-item schema: the wire shape that
 * `user.library.listItems` returns. Lighter than `librarySchema` — no
 * embedded songs / artists / albums; just summary fields the screen
 * needs to render the library card.
 *
 * Used by the client's durable-cache layer (see
 * `apps/mobile/utils/durableCache`) to parse cached rows on read. If the
 * wire shape changes incompatibly, cached rows from older app builds
 * fail safeParse and are purged automatically; the next successful
 * fetch repopulates with the new shape.
 *
 * Drift between this schema and the procedure's TS return type is
 * caught at compile time by the client-side assertion in
 * `apps/mobile/hooks/trpc/useLibrary/queryFunctions.ts`.
 */
const libraryAlbumWireSchema = z.object({
  type: z.literal("album"),
  id: z.string(),
  title: z.string(),
  image: z.string().nullable(),
  artistDisplayText: z.string(),
  privateSongIds: z.array(z.string()),
  created_at: z.string(),
});

const libraryPlaylistWireSchema = z.object({
  type: z.literal("playlist"),
  id: z.string(),
  title: z.string(),
  images: z.array(z.string()),
  songIds: z.array(z.string()),
  user: z.object({ id: z.string(), name: z.string() }),
  visibility: z.enum(["public", "private"]),
  privateSongIds: z.array(z.string()),
  created_at: z.string(),
});

const libraryArtistWireSchema = z.object({
  type: z.literal("artist"),
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  created_at: z.string(),
});

export const libraryListItemSchema = z.discriminatedUnion("type", [
  libraryAlbumWireSchema,
  libraryPlaylistWireSchema,
  libraryArtistWireSchema,
]);

export const libraryListSchema = z.array(libraryListItemSchema);

export type LibraryListItem = z.infer<typeof libraryListItemSchema>;
